import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, BellOff, X, Check, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import {
    pushSupported,
    pushPermission,
    getPushSubscription,
    subscribeToPush,
    unsubscribeFromPush,
} from "@/lib/registerSW";

const DISMISS_KEY = "pr_push_prompt_dismissed_v1";
const PROMPT_DELAY_MS = 8000; // don't nag users the second they land

/**
 * Floating "enable notifications" prompt.
 *
 * Lifecycle:
 *   • If the browser doesn't support push OR the user already
 *     subscribed OR the user permanently dismissed the prompt → render nothing.
 *   • Otherwise, ~8 s after mount, slide in a small card in the bottom-right
 *     asking the user to enable push notifications.
 *   • Clicking "Włącz" calls Notification.requestPermission(), subscribes via
 *     PushManager, and POSTs the subscription to the backend.
 *   • Clicking X remembers the dismissal in localStorage for 30 days.
 *
 * The component is intentionally quiet: no toast spam, no forcing after
 * dismissal, and it disappears entirely once the user is subscribed.
 */
export default function PushPrompt() {
    const [visible, setVisible] = useState(false);
    const [subscribed, setSubscribed] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!pushSupported()) return undefined;
        if (pushPermission() === "denied") return undefined;

        // Skip if user already dismissed the prompt in the last 30 days
        try {
            const raw = localStorage.getItem(DISMISS_KEY);
            if (raw) {
                const at = parseInt(raw, 10);
                if (!Number.isNaN(at) && Date.now() - at < 30 * 24 * 3600 * 1000) {
                    return undefined;
                }
            }
        } catch {
            /* ignore */
        }

        let cancelled = false;
        (async () => {
            const sub = await getPushSubscription();
            if (sub) {
                if (!cancelled) setSubscribed(true);
                return;
            }
            // Delay showing the prompt so it doesn't compete with the hero animation.
            const t = setTimeout(() => {
                if (!cancelled) setVisible(true);
            }, PROMPT_DELAY_MS);
            // Cleanup timer if component unmounts early.
            return () => clearTimeout(t);
        })();

        return () => {
            cancelled = true;
        };
    }, []);

    const dismiss = () => {
        try {
            localStorage.setItem(DISMISS_KEY, String(Date.now()));
        } catch {
            /* ignore */
        }
        setVisible(false);
    };

    const enable = async () => {
        setBusy(true);
        setError("");
        try {
            await subscribeToPush(api);
            setSubscribed(true);
            setTimeout(() => setVisible(false), 1400);
        } catch (e) {
            setError(e?.message || "Nie udało się włączyć powiadomień.");
        } finally {
            setBusy(false);
        }
    };

    if (!pushSupported()) return null;

    return (
        <AnimatePresence>
            {visible && !subscribed && (
                <motion.div
                    key="push-prompt"
                    data-testid="push-prompt"
                    initial={{ opacity: 0, y: 40, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 20, scale: 0.96 }}
                    transition={{ type: "spring", stiffness: 220, damping: 24 }}
                    className="fixed z-[70] bottom-5 right-5 max-w-[340px] w-[calc(100vw-2.5rem)] sm:w-[340px] rounded-lg border border-[color:var(--border-strong)] bg-[color:var(--bg-surface)] shadow-2xl backdrop-blur-md overflow-hidden"
                    style={{ boxShadow: "0 20px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(250,204,21,0.15)" }}
                >
                    <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full blur-3xl pointer-events-none bg-[color:var(--primary)]/20" />
                    <button
                        onClick={dismiss}
                        aria-label="Zamknij"
                        data-testid="push-prompt-dismiss"
                        className="absolute top-2 right-2 w-7 h-7 rounded-md flex items-center justify-center text-[color:var(--text-secondary)] hover:text-white hover:bg-white/10 transition"
                    >
                        <X size={14} />
                    </button>

                    <div className="relative p-5">
                        <div className="flex items-start gap-3 mb-3">
                            <div className="w-10 h-10 rounded-md flex items-center justify-center bg-[color:var(--primary)]/15 text-[color:var(--primary)] shrink-0">
                                <Bell size={20} strokeWidth={1.8} />
                            </div>
                            <div className="pt-0.5">
                                <div className="font-display uppercase tracking-wider text-white text-sm mb-0.5">
                                    Bądź na bieżąco
                                </div>
                                <div className="text-xs text-[color:var(--text-secondary)] leading-relaxed">
                                    Włącz powiadomienia, żeby dostać info
                                    o nowych wersjach moda i ogłoszeniach.
                                </div>
                            </div>
                        </div>

                        {error && (
                            <div className="mb-3 text-xs text-red-400" data-testid="push-prompt-error">
                                {error}
                            </div>
                        )}

                        <div className="flex gap-2 mt-4">
                            <button
                                onClick={enable}
                                disabled={busy}
                                data-testid="push-prompt-enable"
                                className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md bg-[color:var(--primary)] text-[#040914] text-xs font-bold uppercase tracking-[0.15em] hover:brightness-110 transition disabled:opacity-60"
                            >
                                {busy ? (
                                    <>
                                        <Loader2 size={13} className="animate-spin" />
                                        Aktywuję…
                                    </>
                                ) : (
                                    <>
                                        <Bell size={13} strokeWidth={2.4} />
                                        Włącz
                                    </>
                                )}
                            </button>
                            <button
                                onClick={dismiss}
                                data-testid="push-prompt-decline"
                                className="px-3 py-2 rounded-md border border-white/10 text-[color:var(--text-secondary)] text-xs uppercase tracking-[0.15em] hover:text-white hover:border-white/25 transition"
                            >
                                Nie teraz
                            </button>
                        </div>
                    </div>
                </motion.div>
            )}

            {subscribed && visible && (
                <motion.div
                    key="push-prompt-success"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 20 }}
                    className="fixed z-[70] bottom-5 right-5 flex items-center gap-2 px-4 py-3 rounded-md border border-emerald-500/40 bg-[color:var(--bg-surface)] text-emerald-300 text-xs uppercase tracking-[0.15em]"
                    data-testid="push-prompt-success"
                >
                    <Check size={14} />
                    Powiadomienia włączone
                </motion.div>
            )}
        </AnimatePresence>
    );
}

/** Exported so admin/settings pages can show a manual toggle. */
export function usePushState() {
    const [state, setState] = useState({ supported: false, permission: "default", subscribed: false });
    useEffect(() => {
        let cancelled = false;
        (async () => {
            const sub = await getPushSubscription();
            if (cancelled) return;
            setState({
                supported: pushSupported(),
                permission: pushPermission(),
                subscribed: !!sub,
            });
        })();
        return () => {
            cancelled = true;
        };
    }, []);
    return state;
}

// Silence unused-import lint (BellOff is exported for future opt-out UIs).
export const _icons = { BellOff };
